import { isNotDefined } from '@sgme/fp';
import classnames from 'classnames';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { IWorkflowError } from 'utils/errors/workflowErrors';

import type { WorkflowErrorDetailsItemProps } from './WorkflowErrorDetailsItem';

const id = 'errors-panel-details-list-error-message';

/**
 * @depreciated use <WorkflowErrorDetailsItem /> instead
 */
export const WorkflowErrorDetailsItemLegacy: FC<WorkflowErrorDetailsItemProps> = ({
  useLegacyWorkFlowError = false,
  workflowError,
}) => {
  const { formatMessage, formatNumber } = useIntl();

  const modifiedWorkflowError = useMemo(
    () => (useLegacyWorkFlowError ? mapToLegacyWorkflowError(workflowError) : workflowError),
    [workflowError, useLegacyWorkFlowError],
  );

  const getFormattedValue = useCallback(
    (key: keyof (IWorkflowError | NextWorkflowError)) => {
      const value = modifiedWorkflowError[key] as any;

      if (isNotDefined(value)) {
        return '-';
      }
      if (typeof value === 'string') {
        return value.replace(/:/gi, ': ');
      }

      if (typeof value === 'number') {
        return formatNumber(value);
      }

      return value;
    },
    [modifiedWorkflowError, formatNumber],
  );

  const errorKeys = useMemo(() => Object.keys(modifiedWorkflowError), [modifiedWorkflowError]);

  // See https://stackoverflow.com/a/13850750
  // Otherwise, some error messages containing the long types force the message to overflow.
  const forceLineBreaks = {
    wordBreak: 'break-word' as const,
  };

  return (
    <table className={`${id}-component table table-sm mb-0`} data-e2e={id} id={id}>
      <tbody>
        {errorKeys.map(key => (
          <tr
            key={key}
            className={classnames({
              'text-danger': key.toLowerCase() === 'originmessage',
            })}
          >
            <td className="w-25" data-e2e={`error-details-${key.toLowerCase()}-key`}>
              {formatMessage({ id: `errorDetails.${key}` as any, defaultMessage: key })}
            </td>
            <td className="w-75" style={forceLineBreaks} data-e2e={`error-details-${key.toLowerCase()}-value`}>
              {getFormattedValue(key as keyof (IWorkflowError | NextWorkflowError))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

function mapToLegacyWorkflowError(error: NextWorkflowError): IWorkflowError {
  return {
    RfqId: error?.rfqId,
    RejectionType: error?.rejectionType,
    RejectionCode: error?.rejectionCode,
    RejectionOrigin: error?.rejectionOrigin,
    RejectionMessage: error?.rejectionMessage,
    RejectionMessageCode: error?.rejectionMessageCode,
    OriginMessage: error?.originMessage,
    OutOfRangeLowLimit: error?.outOfRangeLowLimit,
    OutOfRangeHighLimit: error?.outOfRangeHighLimit,
    OutOfRangeCondition: error?.outOfRangeCondition,
  };
}
